import { Alert, Flex, Form, Input, Modal, Select, Typography } from 'antd';
import type React from 'react';
import { useEffect, useState } from 'react';
import type { Return as Fachgebiet } from '../../../@types/native-api/generated/basev17';
import type { Return1 as Behandlungsfall, Return2 as SVT } from '../../../@types/native-api/generated/fdasv1';
import type { ErgebnisKonsultation } from '../../../@types/native-api/generated/ksev20';
import { useEcardStore } from '../../../hooks/store/useEcardStore.ts';
import { buildErrorDetails, ecardEnv, getCardSlot } from '../../../utils/ginoHelpers.ts';

export const EcardKonsultationModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onKonsultationCompleted: (data: ErgebnisKonsultation) => void;
}> = ({ open, onClose, onKonsultationCompleted }) => {
  const [svnr, setSvnr] = useState('');
  const [svts, setSvts] = useState<SVT[]>([]);
  const [svtCode, setSvtCode] = useState<string | null>(null);
  const [fachgebiete, setFachgebiete] = useState<Fachgebiet[]>([]);
  const [fachgebietCode, setFachgebietCode] = useState<string>();
  const [behandlungsfaelle, setBehandlungsfaelle] = useState<Behandlungsfall[]>([]);
  const [behandlungsfallCode, setBehandlungsfallCode] = useState<string>();
  const { dialog, config } = useEcardStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const close = () => {
    setSvnr('');
    setError('');
    onClose();
  };

  useEffect(() => {
    if (!dialog) {
      return;
    }
    const fetch = async () => {
      const response = await window.nativeApi?.ecardSvts(ecardEnv());
      setSvts(response?.data?.return ?? []);
    };
    fetch();
  }, [dialog]);

  useEffect(() => {
    if (!dialog || !config) {
      return;
    }
    const fetch = async () => {
      const response = await window.nativeApi?.ecardFachgebieteByOrd(ecardEnv(), {
        dialogId: dialog.dialogId,
        ordId: config.ordinationId,
        taetigkeitsBereichId: config.taetigkeitsBereichId,
      });

      const fachgebiete = response?.data?.return ?? [];
      setFachgebiete(fachgebiete);
      if (fachgebiete[0]) {
        setFachgebietCode(fachgebiete[0].code);
      }
    };
    fetch();
  }, [config, dialog]);

  useEffect(() => {
    if (!fachgebietCode) {
      return;
    }
    const fetch = async () => {
      const response = await window.nativeApi?.ecardBehandlungsfaelleByFachgebiet(ecardEnv(), {
        fachgebietCode: fachgebietCode,
      });
      const behandlungsfaelle = response?.data?.return ?? [];
      setBehandlungsfaelle(behandlungsfaelle);
      if (behandlungsfaelle[0]?.baseProperty) {
        setBehandlungsfallCode(behandlungsfaelle[0].baseProperty.code);
      }
    };
    fetch();
  }, [fachgebietCode]);

  const doKonsultation = async () => {
    setError('');

    if (!config || !dialog) {
      setError('Konfiguration nicht gefunden');
      return;
    }

    setLoading(true);

    try {
      const response = await window.nativeApi!.ginoStatus(
        config.cardReaderAddress,
        svnr
          ? {
              sigType: 'sv',
              svnr: svnr,
              returnToken: true,
            }
          : {
              sigType: 'sv',
              vpnr: config.vpnr,
              returnToken: true,
            }
      );

      if (response.error) {
        setError(`Fehler beim Lesen der Karte. ${buildErrorDetails(response)}`);
        return;
      }

      const slot = getCardSlot(response, svnr ? 'o-card' : 'e-card');
      const token = slot?.token;
      if (!token) {
        setError('Fehler beim Lesen der Karte. Es konnte keine gültige Signatur ausgestellt werden. Ist die korrekte Karte gesteckt?');
        return;
      }

      try {
        const response = await window.nativeApi?.ecardDoKonsultation(ecardEnv(), {
          dialogId: dialog.dialogId,
          svNummer: svnr,
          svtCode: svtCode ?? undefined,
          fachgebietsCode: fachgebietCode,
          behandlungsfallCode: behandlungsfallCode,
          cardToken: token.value,
          isOffline: false,
        });

        if (response?.error) {
          setError(`Es ist ein Fehler aufgetreten. ${buildErrorDetails(response)}`);
          return;
        }

        if (!response?.data?.return?.ergebnisKonsultation) {
          setError(`Es konnte kein Konsultationsergebnis abgefragt werden.`);
          return;
        }

        onKonsultationCompleted(response.data.return.ergebnisKonsultation);
      } catch (_e) {
        setError('Es ist ein unbekannter Fehler bei der Anfrage zum e-card System aufgetreten.');
      }
    } catch (_e) {
      setError('Es ist ein unbekannter Fehler bei der Verbindung zum Kartenlesegerät aufgetreten');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Konsultation / Behandlungsfall durchführen"
      open={open && !!config}
      onCancel={close}
      width={600}
      onOk={doKonsultation}
      okButtonProps={{ loading: loading }}
      okText="Konsultation durchführen"
    >
      <Flex vertical gap="middle">
        <Alert
          type="info"
          title={
            <div>
              Wenn eine e-card verfügbar ist, verwenden Sie bitte die e-card am Kartenlesegerät ohne manuelle Eingabe der SV-Nummer.
              <br />
              Wenn keine e-card verfügbar ist, verwenden Sie bitte die Admin-Karte am Kartenlesegerät und geben Sie die SV-Nummer manuell
              an.
            </div>
          }
          showIcon
        />
        <Form labelAlign="left" labelCol={{ flex: '130px' }} colon={false}>
          <Form.Item label="SV-Nummer">
            <Input placeholder="SVNR" autoFocus value={svnr} onChange={e => setSvnr(e.target.value)} />
          </Form.Item>
          <Form.Item label="KV-Träger">
            <Select
              options={svts.map(it => ({
                label: it.kurzbezeichnung,
                value: it.code,
              }))}
              placeholder="Automatische Bestimmung, wenn nicht angegeben"
              style={{ width: '100%' }}
              value={svtCode}
              onChange={v => setSvtCode(v)}
              allowClear
            />
          </Form.Item>
          <Form.Item label="Fachgebiet">
            <Select
              options={fachgebiete.map(it => ({
                label: it.text,
                value: it.code,
              }))}
              style={{ width: '100%' }}
              value={fachgebietCode}
              onChange={v => setFachgebietCode(v)}
            />
          </Form.Item>
          <Form.Item label="Behandlungsfall">
            <Select
              options={behandlungsfaelle.map(it => ({
                label: it.baseProperty?.text,
                value: it.baseProperty?.code,
              }))}
              style={{ width: '100%' }}
              value={behandlungsfallCode}
              onChange={v => setBehandlungsfallCode(v)}
            />
          </Form.Item>
        </Form>
      </Flex>
      <Typography.Text type="danger">{error}</Typography.Text>
    </Modal>
  );
};
